import React from 'react';
import { Box, Typography } from '@mui/material';

export const CARD_WIDTH = 380;
export const CARD_HEIGHT = 640;

export const DEFAULT_CARD_LAYOUT = {
    front: {
        watermark: { x: 23, y: 128, w: 334, h: 334, opacity: 0.12 },
        header: { x: 0, y: 0, w: 380, h: 78 },
        photo: { x: 95, y: 90, w: 190, h: 190 },
        name: { x: 0, y: 290, w: 380, h: 36, fontSize: 20 },
        details: { x: 72, w: 270, startY: 330, rowGap: 0, labelWidth: 80, colonWidth: 12, labelAlign: 'left', valueAlign: 'left' },
        address: { x: 72, y: 330, w: 270, h: 62, fontSize: 15 },
        dob: { x: 72, y: 393, w: 270, h: 30, fontSize: 18 },
        doj: { x: 72, y: 423, w: 270, h: 30, fontSize: 18 },
        doi: { x: 72, y: 453, w: 270, h: 30, fontSize: 18 },
        bloodGroup: { x: 72, y: 483, w: 270, h: 30, fontSize: 18 },
        idBadge: { x: 120, y: 525, w: 140, h: 34, fontSize: 24 },
        authSignImage: { x: 25, y: 565, w: 125, h: 43 },
        authSignLabel: { x: 25, y: 608, w: 125, h: 19, fontSize: 16 },
        authSign: { x: 25, y: 565, w: 125, h: 62, fontSize: 16 },
        empSign: { x: 230, y: 585, w: 110, h: 42, fontSize: 16 },
    },
    back: {
        watermark: { x: 23, y: 128, w: 334, h: 334, opacity: 0.12 },
        header: { x: 0, y: 0, w: 380, h: 78 },
        address: { x: 10, y: 105, w: 360, h: 285, fontSize: 30 },
        emergency: { x: 45, y: 430, w: 290, h: 40, fontSize: 27 },
        phone: { x: 20, y: 482, w: 340, h: 34, fontSize: 23 },
        mobile: { x: 20, y: 517, w: 340, h: 34, fontSize: 23 },
    },
};

const cardSceneStyle = {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    perspective: '1500px',
    cursor: 'pointer',
};

const cardFlipperStyle = (isFlipped) => ({
    width: '100%',
    height: '100%',
    position: 'relative',
    transformStyle: 'preserve-3d',
    transition: 'transform 0.8s ease',
    transform: isFlipped ? 'rotateY(180deg)' : 'none',
});

const cardFaceBase = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    backfaceVisibility: 'hidden',
    overflow: 'hidden',
    border: '2px solid #081B8E',
    boxShadow: 'none',
    background: '#fff',
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const itemStyle = (item, extra = {}) => ({
    position: 'absolute',
    left: item.x,
    top: item.y,
    width: item.w,
    height: item.h,
    boxSizing: 'border-box',
    ...extra,
});

function EditableItem({
    id,
    layout,
    side = 'front',
    editMode,
    selected,
    onSelect,
    onDragStart,
    onResizeStart,
    children,
    sx = {},
}) {
    const item = layout?.[id] || { x: 0, y: 0, w: 100, h: 40 };

    return (
        <Box
            data-layout-id={`${side}:${id}`}
            onPointerDown={(event) => {
                if (!editMode) return;
                event.stopPropagation();
                onSelect?.(id);
                onDragStart?.(event, id);
            }}
            sx={{
                ...itemStyle(item),
                outline: editMode && selected ? '2px solid #1976d2' : 'none',
                outlineOffset: editMode && selected ? '2px' : 0,
                cursor: editMode ? 'move' : 'default',
                touchAction: editMode ? 'none' : 'auto',
                userSelect: editMode ? 'none' : 'auto',
                ...sx,
            }}
        >
            {children}
            {editMode && selected && (
                <>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -24,
                            left: -2,
                            px: 0.75,
                            py: 0.15,
                            borderRadius: '4px 4px 0 0',
                            background: '#1976d2',
                            color: '#fff',
                            fontSize: 10,
                            lineHeight: 1.5,
                            zIndex: 30,
                            pointerEvents: 'none',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {id}
                    </Box>
                    <Box
                        data-resize-handle={id}
                        onPointerDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onSelect?.(id);
                            onResizeStart?.(event, id);
                        }}
                        sx={{
                            position: 'absolute',
                            width: 10,
                            height: 10,
                            right: -5,
                            bottom: -5,
                            borderRadius: '50%',
                            background: '#1976d2',
                            border: '2px solid #fff',
                            boxShadow: '0 1px 4px rgba(0,0,0,.25)',
                            cursor: 'nwse-resize',
                            zIndex: 40,
                        }}
                    />
                </>
            )}
        </Box>
    );
}

const FrontBackground = ({ layout, editMode, selectedId, onSelect }) => {
    const item = layout?.watermark || DEFAULT_CARD_LAYOUT.front.watermark;

    return (
        <Box
            sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: '#fff',
                overflow: 'hidden',
            }}
        >
            <EditableItem
                id="watermark"
                side="front"
                layout={layout}
                editMode={editMode}
                selected={selectedId === 'watermark'}
                onSelect={onSelect}
                sx={{ zIndex: 0, pointerEvents: editMode ? 'auto' : 'none' }}
            >
                <Box
                    component="img"
                    src="/sswhite-w-transparent.png"
                    alt="Watermark"
                    draggable={false}
                    sx={{
                        width: '100%',
                        height: '100%',
                        transform: 'none',
                        objectFit: 'contain',
                        opacity: item.opacity ?? 0.12,
                        display: 'block',
                        pointerEvents: 'none',
                    }}
                />
            </EditableItem>
        </Box>
    );
};

const BackBackground = ({ layout, editMode, selectedId, onSelect }) => {
    const item = layout?.watermark || DEFAULT_CARD_LAYOUT.back.watermark;

    return (
        <Box
            sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: '#fff',
                overflow: 'hidden',
            }}
        >
            <EditableItem
                id="watermark"
                side="back"
                layout={layout}
                editMode={editMode}
                selected={selectedId === 'watermark'}
                onSelect={onSelect}
                sx={{ zIndex: 0, pointerEvents: editMode ? 'auto' : 'none' }}
            >
                <Box
                    component="img"
                    src="/sswhite-w-transparent.png"
                    alt="Watermark"
                    draggable={false}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        opacity: item.opacity ?? 0.12,
                        display: 'block',
                        pointerEvents: 'none',
                    }}
                />
            </EditableItem>
        </Box>
    );
};

export default function OfficeIdCard({
    data,
    isFlipped,
    frontRef,
    backRef,
    onFlip,
    layout = DEFAULT_CARD_LAYOUT,
    editMode = false,
    selectedId = null,
    onSelectElement,
    onElementDragStart,
    onElementResizeStart,
    activeDesignSide = 'front',
}) {
    const {
        empName,
        empId,
        dob,
        doj,
        doi,
        bloodGroup,
        empPhotoUrl,
        signatureUrl,
        logoUrl,
        companyAddressFront,
        companyAddressBack,
        emergencyPhone,
        emergencyMobile,
    } = data || {};

    const formatDate = (date) => {
        if (!date) return '';
        return String(date).split(' ')[0];
    };

    const front = layout?.front || DEFAULT_CARD_LAYOUT.front;
    const back = layout?.back || DEFAULT_CARD_LAYOUT.back;
    const details = { ...DEFAULT_CARD_LAYOUT.front.details, ...(front.details || {}) };

    return (
        <Box
            sx={{
                ...cardSceneStyle,
                cursor: editMode ? 'default' : 'pointer',
            }}
            onClick={() => {
                if (!editMode) onFlip?.();
            }}
            className="office-id-card-scene"
        >
            <Box sx={cardFlipperStyle(isFlipped)} className="office-id-card-flipper">
                <Box
                    ref={frontRef}
                    sx={{
                        ...cardFaceBase,
                        transform: 'none',
                        pointerEvents: editMode && activeDesignSide !== 'front' ? 'none' : 'auto',
                    }}
                    className="office-id-card-face"
                >
                    <FrontBackground
                        layout={front}
                        editMode={editMode}
                        selectedId={selectedId}
                        onSelect={onSelectElement}
                onDragStart={onElementDragStart}
                onResizeStart={onElementResizeStart}
                    />

                    <Box sx={{ position: 'relative', zIndex: 2, width: '100%', height: '100%' }}>
                        <EditableItem
                            id="header"
                            side="front"
                            layout={front}
                            editMode={editMode}
                            selected={selectedId === 'header'}
                            onSelect={onSelectElement}
                onDragStart={onElementDragStart}
                onResizeStart={onElementResizeStart}
                            sx={{ zIndex: 5, overflow: 'hidden' }}
                        >
                            <Box
                                component="img"
                                src={logoUrl}
                                alt="Company Header"
                                draggable={false}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                    pointerEvents: 'none',
                                }}
                            />
                        </EditableItem>

                        <EditableItem
                            id="photo"
                            side="front"
                            layout={front}
                            editMode={editMode}
                            selected={selectedId === 'photo'}
                            onSelect={onSelectElement}
                onDragStart={onElementDragStart}
                onResizeStart={onElementResizeStart}
                            sx={{ overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.12)' }}
                        >
                            {empPhotoUrl && (
                                <Box
                                    component="img"
                                    key={empPhotoUrl}
                                    src={empPhotoUrl}
                                    alt={empName || 'Employee'}
                                    draggable={false}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                        pointerEvents: 'none',
                                    }}
                                    onError={(event) => {
                                        const image = event.currentTarget;
                                        if (
                                            image.src.includes('dashboardapi.sswhite.co') &&
                                            empId
                                        ) {
                                            image.onerror = null;
                                            image.src = `https://hrms.sswhite.co/pic/${empId}.png`;
                                            return;
                                        }
                                        image.onerror = null;
                                        image.style.display = 'none';
                                    }}
                                />
                            )}
                        </EditableItem>

                        <EditableItem
                            id="name"
                            side="front"
                            layout={front}
                            editMode={editMode}
                            selected={selectedId === 'name'}
                            onSelect={onSelectElement}
                onDragStart={onElementDragStart}
                onResizeStart={onElementResizeStart}
                            sx={{ px: 3, textAlign: 'center' }}
                        >
                            <Typography
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    color: '#002b7a',
                                    fontWeight: 900,
                                    fontSize: `${front.name.fontSize || 20}px`,
                                    letterSpacing: '0.03em',
                                    fontFamily: 'Arial, Helvetica, sans-serif',
                                    textTransform: 'uppercase',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    lineHeight: 1.15,
                                }}
                            >
                                {empName}
                            </Typography>
                        </EditableItem>

                        {[
                            ['address', 'ADD', companyAddressFront],
                            ['dob', 'D.O.B', formatDate(dob)],
                            ['doj', 'D.O.J', formatDate(doj)],
                            ['doi', 'D.O.I', formatDate(doi)],
                            ['bloodGroup', 'Bld Grp', bloodGroup],
                        ].map(([id, label, value]) => (
                            <EditableItem
                                key={id}
                                id={id}
                                side="front"
                                layout={front}
                                editMode={editMode}
                                selected={selectedId === id}
                                onSelect={onSelectElement}
                                onDragStart={onElementDragStart}
                                onResizeStart={onElementResizeStart}
                                sx={{ display: 'flex', alignItems: 'flex-start' }}
                            >
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: `${details.labelWidth}px ${details.colonWidth}px minmax(0,1fr)`,
                                        width: '100%',
                                        height: '100%',
                                        fontSize: `${front[id].fontSize || 18}px`,
                                        lineHeight: 1.2,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            fontSize: `${id === 'address' ? 18 : front[id].fontSize || 18}px`,
                                            textAlign: details.labelAlign || 'left',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {label}
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>:</Box>
                                    <Box
                                        sx={{
                                            fontSize: `${front[id].fontSize || 18}px`,
                                            textAlign: details.valueAlign || 'left',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {value}
                                    </Box>
                                </Box>
                            </EditableItem>
                        ))}

                        <EditableItem
                            id="idBadge"
                            side="front"
                            layout={front}
                            editMode={editMode}
                            selected={selectedId === 'idBadge'}
                            onSelect={onSelectElement}
                onDragStart={onElementDragStart}
                onResizeStart={onElementResizeStart}
                            sx={{
                                bgcolor: '#081B8E',
                                color: '#fff',
                                minWidth: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 900,
                                letterSpacing: '1px',
                                fontSize: `${front.idBadge.fontSize || 24}px`,
                                overflow: 'hidden',
                            }}
                        >
                            ID : {empId}
                        </EditableItem>

                        <EditableItem
                            id="authSignImage"
                            side="front"
                            layout={front}
                            editMode={editMode}
                            selected={selectedId === 'authSignImage'}
                            onSelect={onSelectElement}
                            onDragStart={onElementDragStart}
                            onResizeStart={onElementResizeStart}
                            sx={{ overflow: 'hidden' }}
                        >
                            <Box
                                component="img"
                                src={signatureUrl}
                                alt="Authorized Signature"
                                draggable={false}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    display: 'block',
                                    pointerEvents: 'none',
                                }}
                            />
                        </EditableItem>

                        <EditableItem
                            id="authSignLabel"
                            side="front"
                            layout={front}
                            editMode={editMode}
                            selected={selectedId === 'authSignLabel'}
                            onSelect={onSelectElement}
                            onDragStart={onElementDragStart}
                            onResizeStart={onElementResizeStart}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                            }}
                        >
                            <Typography sx={{ fontSize: `${front.authSignLabel?.fontSize || front.authSign?.fontSize || 16}px`, fontWeight: 500, lineHeight: 1.1, width: '100%', textAlign: 'center' }}>
                                Auth Sign
                            </Typography>
                        </EditableItem>

                        <EditableItem
                            id="empSign"
                            side="front"
                            layout={front}
                            editMode={editMode}
                            selected={selectedId === 'empSign'}
                            onSelect={onSelectElement}
                onDragStart={onElementDragStart}
                onResizeStart={onElementResizeStart}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                overflow: 'hidden',
                            }}
                        >
                            <Typography sx={{ fontSize: `${front.empSign.fontSize || 16}px`, color: '#081B8E', lineHeight: 1.1 }}>
                                {empId}
                            </Typography>
                            <Typography sx={{ fontSize: `${front.empSign.fontSize || 16}px`, fontWeight: 500, lineHeight: 1.1 }}>
                                Emp Sign
                            </Typography>
                        </EditableItem>
                    </Box>
                </Box>

                <Box
                    ref={backRef}
                    sx={{
                        ...cardFaceBase,
                        transform: 'rotateY(180deg)',
                        pointerEvents: editMode && activeDesignSide !== 'back' ? 'none' : 'auto',
                    }}
                    className="office-id-card-face"
                >
                    <BackBackground
                        layout={back}
                        editMode={editMode}
                        selectedId={selectedId}
                        onSelect={onSelectElement}
                onDragStart={onElementDragStart}
                onResizeStart={onElementResizeStart}
                    />

                    <Box sx={{ position: 'relative', width: '100%', height: '100%', zIndex: 2 }}>
                        <EditableItem
                            id="header"
                            side="back"
                            layout={back}
                            editMode={editMode}
                            selected={selectedId === 'header'}
                            onSelect={onSelectElement}
                onDragStart={onElementDragStart}
                onResizeStart={onElementResizeStart}
                            sx={{ overflow: 'hidden' }}
                        >
                            <Box
                                component="img"
                                src={logoUrl}
                                alt="Company Header"
                                draggable={false}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                    pointerEvents: 'none',
                                }}
                            />
                        </EditableItem>

                        <EditableItem
                            id="address"
                            side="back"
                            layout={back}
                            editMode={editMode}
                            selected={selectedId === 'address'}
                            onSelect={onSelectElement}
                onDragStart={onElementDragStart}
                onResizeStart={onElementResizeStart}
                            sx={{ px: 1, textAlign: 'center', overflow: 'hidden' }}
                        >
                            <Typography
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    textAlign: 'center',
                                    whiteSpace: 'pre-line',
                                    fontFamily: '"Times New Roman", serif',
                                    fontWeight: 700,
                                    fontSize: `${back.address.fontSize || 30}px`,
                                    lineHeight: 1.25,
                                    color: '#000',
                                    letterSpacing: '0.2px',
                                    wordBreak: 'break-word',
                                    overflow: 'hidden',
                                    display: 'block',
                                }}
                            >
                                {companyAddressBack}
                            </Typography>
                        </EditableItem>

                        <EditableItem
                            id="emergency"
                            side="back"
                            layout={back}
                            editMode={editMode}
                            selected={selectedId === 'emergency'}
                            onSelect={onSelectElement}
                onDragStart={onElementDragStart}
                onResizeStart={onElementResizeStart}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                        >
                            <Typography
                                sx={{
                                    fontSize: `${back.emergency.fontSize || 27}px`,
                                    fontWeight: 900,
                                    fontFamily: 'Arial',
                                    color: '#081B8E',
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1.1,
                                }}
                            >
                                Emergency Contact No
                            </Typography>
                        </EditableItem>

                        <EditableItem
                            id="phone"
                            side="back"
                            layout={back}
                            editMode={editMode}
                            selected={selectedId === 'phone'}
                            onSelect={onSelectElement}
                onDragStart={onElementDragStart}
                onResizeStart={onElementResizeStart}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                        >
                            <Typography
                                sx={{
                                    fontSize: `${back.phone.fontSize || 23}px`,
                                    fontWeight: 700,
                                    fontFamily: 'Arial, sans-serif',
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1.1,
                                }}
                            >
                                Phone : {emergencyPhone}
                            </Typography>
                        </EditableItem>

                        <EditableItem
                            id="mobile"
                            side="back"
                            layout={back}
                            editMode={editMode}
                            selected={selectedId === 'mobile'}
                            onSelect={onSelectElement}
                onDragStart={onElementDragStart}
                onResizeStart={onElementResizeStart}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                        >
                            <Typography
                                sx={{
                                    fontSize: `${back.mobile.fontSize || 23}px`,
                                    fontWeight: 700,
                                    fontFamily: 'Arial, sans-serif',
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1.1,
                                }}
                            >
                                Mobile : {emergencyMobile}
                            </Typography>
                        </EditableItem>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
